import {
  Context,
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useSearchParams, createSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import useCrud, { IHandleNavigate } from '../hooks/useCrud';
import useMultiDelete from '../hooks/useMultiDelete';
import useMultiUpdate from '../hooks/useMultiUpdate';
import { tableReducer } from '../reducers/TableReducer';
import { IArrayResponse } from '../../../../api-client';
import { pageSizeOptions } from '../../../../../global';
import { IDefaultValues, IInitialState, INavigationPagination } from '../../../types';

interface TableContextProps {
  data: any;
  state: IInitialState;
  isLoading: boolean;
  navigation: INavigationPagination | undefined;
  dispatch: Dispatch<any>;
  handlerNavigate: (prop: IHandleNavigate) => void;
  handlerUpdate?: (
    data: any[],
    rowChecked: string[],
    payload: any,
    cb?: (entity: any, payload: any) => any
  ) => any;
  handlerDelete?: (rowChecked: string[]) => any;
}

const defaultNavigationView = {
  itemsPerPage: 10,
  totalItems: 0,
  currentPage: 1,
  first: null,
  previous: null,
  next: null,
  last: null,
};

const extractedHydraView = (data?: IArrayResponse<any>): INavigationPagination => {
  if (!data) {
    return defaultNavigationView;
  }

  let currentPage;
  try {
    const page = data['hydra:view']['@id'].split('=').pop();
    if (data['hydra:view']['@id']?.search(/page=/) === -1) {
      throw new Error('no page');
    }
    currentPage = Number.isInteger(Number(page)) ? Number(page) : 1;
  } catch (e) {
    currentPage = 1;
  }
  return {
    currentPage,
    totalItems: data['hydra:totalItems'] || 0,
    first: data['hydra:view']['hydra:first'] || null,
    previous: data['hydra:view']['hydra:previous'] || null,
    next: data['hydra:view']['hydra:next'] || null,
    last: data['hydra:view']['hydra:last'] || null,
  };
};

const TableContext: Context<any | null> = createContext<any | null>(null);

function generateFilters(enableSearchParams: boolean, searchParams: any) {
  const searchParamFilters: any[] = [];
  const internalSearchParamFilters: any[] = [];
  let searchParamStr = '';
  if (enableSearchParams) {
    [...searchParams].forEach(([property, value]) => {
      if (
        !(
          property === 'createdAt[after]' ||
          property === 'createdAt[before]' ||
          property === 'declarationDate[after]' ||
          property === 'declarationDate[before]' ||
          property === 'requestStatusDate[after]' ||
          property === 'requestStatusDate[before]'
        )
      ) {
        internalSearchParamFilters.push({ property, value });
      }
      searchParamFilters.push({ property, value });
    });
    searchParamStr = `&${createSearchParams(Object.fromEntries([...searchParams]))}`;

    const createdAtFilterValue: Record<string, any> = {};
    const createdAfter = searchParamFilters.find(filter => filter.property === 'createdAt[after]');
    const createdBefore = searchParamFilters.find(
      filter => filter.property === 'createdAt[before]'
    );

    const declarationDateFilterValue: Record<string, any> = {};
    const declarationDateAfter = searchParamFilters.find(
      filter => filter.property === 'declarationDate[after]'
    );
    const declarationDateBefore = searchParamFilters.find(
      filter => filter.property === 'declarationDate[before]'
    );

    const requestStatusDateFilterValue: Record<string, any> = {};
    const requestStatusDateAfter = searchParamFilters.find(
      filter => filter.property === 'requestStatusDate[after]'
    );
    const requestStatusDateBefore = searchParamFilters.find(
      filter => filter.property === 'requestStatusDate[before]'
    );

    if (createdAfter) {
      createdAtFilterValue.startDate = createdAfter.value;
    }
    if (createdBefore) {
      createdAtFilterValue.endDate = createdBefore.value;
    }
    if (declarationDateAfter) {
      declarationDateFilterValue.startDate = declarationDateAfter.value;
    }
    if (declarationDateBefore) {
      declarationDateFilterValue.endDate = declarationDateBefore.value;
    }
    if (requestStatusDateAfter) {
      requestStatusDateFilterValue.startDate = requestStatusDateAfter.value;
    }
    if (requestStatusDateBefore) {
      requestStatusDateFilterValue.endDate = requestStatusDateBefore.value;
    }
    if (Object.keys(createdAtFilterValue).length) {
      internalSearchParamFilters.push({ property: 'createdAt', value: createdAtFilterValue });
    }
    if (Object.keys(declarationDateFilterValue).length) {
      internalSearchParamFilters.push({
        property: 'declarationDate',
        value: declarationDateFilterValue,
      });
    }
    if (Object.keys(requestStatusDateFilterValue).length) {
      internalSearchParamFilters.push({
        property: 'requestStatusDate',
        value: requestStatusDateFilterValue,
      });
    }
  }

  return [searchParamFilters, internalSearchParamFilters, searchParamStr];
}

export const TableProvider = ({
  children,
  defaultValues,
  enableSearchParams = false,
}: {
  children: ReactNode;
  defaultValues: IDefaultValues;
  enableSearchParams?: boolean;
}) => {
  const [searchParams, setSearchParams] = useSearchParams(defaultValues.defaultSearchParams);
  const generatedFilters = useMemo(
    () => generateFilters(enableSearchParams, searchParams),
    [enableSearchParams, searchParams]
  );
  const searchParamFilters: any[] = generatedFilters[0] as any[];
  const internalSearchParamFilters: any[] = generatedFilters[1] as any[];
  const searchParamStr: string = generatedFilters[2] as string;
  const queryClient = useQueryClient();

  const createInitialState = useCallback(
    (defaultValues: IDefaultValues) => ({
      path: `${defaultValues.basePath}&itemsPerPage=${defaultValues.itemsPerPage}${
        defaultValues.defaultSort ? `&${defaultValues.defaultSort}` : ''
      }${searchParamStr}`,
      filters: internalSearchParamFilters,
      searchParamFilters,
      sorts: [],
      basePath: defaultValues?.basePath,
      isPagination: defaultValues?.isPagination ?? true,
      itemsPerPage: defaultValues?.itemsPerPage ?? 10,
      pageSizeOptions: defaultValues?.pageSizeOptions ?? pageSizeOptions,
      rowChecked: [],
    }),
    [internalSearchParamFilters, searchParamFilters, searchParamStr]
  );
  const [state, dispatch] = useReducer(tableReducer, defaultValues, createInitialState);

  const response = useQuery<IArrayResponse<any>, Error>(
    [defaultValues.queryKey, `${state.path}`],
    defaultValues.queryFunc,
    { refetchOnWindowFocus: false }
  );

  const { handlerNavigate } = useCrud({
    queryKey: defaultValues.queryKey,
    pathPrefix: defaultValues.pathPrefix,
    deleteAction: defaultValues.deleteAction,
    updateAction: defaultValues.updateAction,
  });

  const { handlerDelete } = useMultiDelete({
    queryKey: defaultValues.queryKey,
    queryClient,
  });

  const { handlerUpdate } = useMultiUpdate({
    queryClient,
    queryClientKey: defaultValues.queryKey,
  });

  useEffect(() => {
    const updatedSearchParams = state.searchParamFilters.reduce(
      (obj, item) => ({ ...obj, [item.property]: item.value }),
      {}
    );
    setSearchParams(updatedSearchParams);
  }, [state.searchParamFilters, setSearchParams]);

  const value: TableContextProps = useMemo(() => {
    const data =
      response?.data && response?.data['hydra:member'] ? response?.data['hydra:member'] : [];
    const navigation = extractedHydraView(response?.data);

    return {
      data,
      state,
      isLoading: response.isLoading,
      navigation,
      dispatch,
      handlerNavigate,
      handlerDelete,
      handlerUpdate,
    };
  }, [response?.data, response.isLoading, handlerNavigate, state, handlerDelete, handlerUpdate]);

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
};

export default TableContext;
